// KitNotesModal — edit notes for a device kit. PATCHes /device-kits/:id.

import { useState } from "react";
import type { JSX } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../lib/api.js";
import { Modal } from "./Modal.js";
import type { DeviceKit } from "./KitRow.js";

interface Props {
  kit:     DeviceKit;
  onClose: () => void;
  onSaved: () => void;
}

export function KitNotesModal({ kit, onClose, onSaved }: Props): JSX.Element {
  const [notes, setNotes] = useState(kit.notes ?? "");
  const qc = useQueryClient();

  const patch = useMutation({
    mutationFn: () =>
      apiFetch(`/device-kits/${kit._id}`, { method: "PATCH", body: JSON.stringify({ notes }) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["device-kits"] });
      onSaved();
    },
  });

  const remaining = 500 - notes.length;

  return (
    <Modal title={`Notes — ${kit.kit_name}`} onClose={onClose}>
      <div className="space-y-4">
        <div>
          <label htmlFor="kit-notes" className="block text-sm font-medium text-slate-700 mb-1">
            Notes
          </label>
          <textarea
            id="kit-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value.slice(0, 500))}
            rows={5}
            placeholder="Optional notes about this kit…"
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-navy-500 resize-none"
          />
          <p className={`text-xs mt-1 text-right ${remaining < 50 ? "text-amber-600" : "text-slate-400"}`}>
            {remaining} chars remaining
          </p>
        </div>
        {patch.isError && (
          <p role="alert" className="text-sm text-red-600">Failed to save notes. Please try again.</p>
        )}
        <div className="flex justify-end gap-3 pt-1">
          <button type="button" onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50">
            Cancel
          </button>
          <button type="button" disabled={patch.isPending} onClick={() => patch.mutate()}
            className="px-4 py-2 text-sm rounded-lg bg-navy-700 text-white font-medium hover:bg-navy-800 disabled:opacity-50">
            {patch.isPending ? "Saving…" : "Save notes"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
